import React, { memo } from "react";
import {
  Grid,
  TextField,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../SharableInputs/Accordion";

// Mapping ugly API field names → Pretty labels
const labelMap = {
  name: "Company Name",
  everifyNumber: "E-Verify Number",
  representativeEverifyNumber: "Representative E-Verify Number",

  contactPersonFirstName: "First Name",
  contactPersonLastName: "Last Name",
  contactPersonPhoneNumber: "Phone Number",
  contactPersonEmail: "Email",

  address: "Address",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  county: "County",

  mailingAddress1: "Mailing Address 1",
  mailingAddress2: "Mailing Address 2",
  mailingCity: "Mailing City",
  mailingState: "Mailing State",
  mailingZip: "Mailing Zip",

  employeeCount: "Employee Count",
  fein: "FEIN",
  naicsCode: "NAICS Code",
  doingBusinessAs: "Doing Business As",
  dunsNumber: "DUNS Number",

  notes: "Notes",
  organizationCategory: "Organization Category",
  companyUEINumber: "UEI Number",
  whichEmployeesEVerify: "Which Employees E-Verify",
  categoryBestDescribes: "Category Best Describes",

  isCanaryRepresentative: "Is Canary Representative?",
};

// Fields we should NOT render as inputs
const hiddenFields = ["status", "isParent", "companyClientID", "id"];

const numericFields = new Set([
  "everifyNumber",
  "representativeEverifyNumber",
  "employeeCount",
  "organizationCategory",
  "whichEmployeesEVerify",
  "categoryBestDescribes",
]);


// ----------- MEMO INPUT (stable) -----------
const MemoInput = memo(function MemoInput({
  field,
  value,
  onChange,
  type = "text",
  required = false,
}) {
  const label = labelMap[field] || field;
  const inputLabelProps = required
    ? {
        required: true,
        sx: {
          "& .MuiFormLabel-asterisk": {
            color: "#DC2626",
          },
        },
      }
    : { required: false };

  return (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        size="small"
        label={label}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        InputLabelProps={inputLabelProps}
      />
    </Grid>
  );
});


// ----------- MEMO CHECKBOX -----------
const MemoCheckbox = memo(function MemoCheckbox({ field, checked, onChange }) {
  return (
    <Grid item xs={12}>
      <FormControlLabel
        control={<Checkbox checked={checked} onChange={onChange} />}
        label={labelMap[field]}
      />
    </Grid>
  );
});


// ----------- PARENT/CHILD SECTION WRAPPER -----------
export const CompanySection = memo(function CompanySection({
  sectionKey,
  title,
  sectionData,
  updateFn,
  openAccordions,
  setOpenAccordions,
  childrenMode = false,
  onDelete = null,
  requiredFields = [],
}) {
  return (
    <Accordion
      type="multiple"
      collapsible
      value={openAccordions}
      onValueChange={setOpenAccordions}
    >
      <AccordionItem value={sectionKey}>
        <AccordionTrigger>{title}</AccordionTrigger>

        <AccordionContent>
          <Grid container spacing={2}>
            {Object.entries(sectionData).map(([field, value]) => {
              // Skip hidden fields
              if (hiddenFields.includes(field)) return null;

              // Checkbox field
              if (field === "isCanaryRepresentative") {
                return (
                  <MemoCheckbox
                    key={field}
                    field={field}
                    checked={!!value}
                    onChange={(e) =>
                      updateFn(field, e.target.checked)
                    }
                  />
                );
              }

              // Regular inputs
              return (
                <MemoInput
                  key={field}
                  field={field}
                  value={value}
                  onChange={(e) => updateFn(field, e.target.value)}
                  type={numericFields.has(field) ? "number" : "text"}
                  required={requiredFields.includes(field)}
                />
              );
            })}
          </Grid>

          {/* Child remove button */}
          {childrenMode && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                startIcon={<DeleteIcon />}
                onClick={onDelete}
                sx={{
                  color: "#DC2626",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "rgba(220,38,38,0.1)" },
                }}
              >
                Remove Child Company
              </Button>
            </Box>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
});
